import { BaseComponentImpl } from '../../../../types/implementations';
import { Building } from '../../../../types/protocols';
import { ComponentLifetime, MaintenanceTemplate, TemplatesByType } from '../../TemplatesEditor/types';
import { formatDateToString } from '../../../../Helpers/maintenanceCostUtils';
import { formatDateForStorage } from '../../../../Helpers/DateUtils';
import type { ComponentMaintenanceDataWithDone, MaintenanceDraftWithDone } from '../types';
import type { MaintenanceActivity } from '../../../../Helpers/maintenanceCostUtils';
import * as calculations from './calculations';
import { newActivityEntry } from './draftSync';
import { templateLookup } from '../../../../Helpers/maintenanceSchedule';
import { templateOfferedTo } from '../../../../Helpers/maintenanceTemplates';

/**
 * Recalculate dates for a component after condition change
 * Preserves all costs and only updates dates based on new condition
 * 
 * @param componentKey - Key of the component to recalculate
 * @param building - Building data containing components
 * @param componentLifetimes - Lifetime configuration for each component type
 * @param maintenanceTemplates - Template configuration for each component type
 * @param maintenanceDraft - Current draft state
 * @returns Updated component data or null if unable to recalculate
 */
export const recalculateDatesFromCondition = (
    componentKey: string,
    building: Building | null,
    componentLifetimes: Record<string, ComponentLifetime>,
    maintenanceTemplates: TemplatesByType,
    maintenanceDraft: MaintenanceDraftWithDone
): ComponentMaintenanceDataWithDone | null => {
    // Read component with condition from building (single source of truth)
    const componentData = building?.model?.structure?.components?.[componentKey];
    if (!componentData) {
        console.error(`Component ${componentKey} not found in building`);
        return null;
    }

    const component = new BaseComponentImpl(componentData);
    const componentType = component.specificType?.value;
    if (!componentType) return null;

    const lifetime = componentLifetimes[componentType];
    if (!lifetime) return null;

    // Recalculate replacement year from condition (needed for both new and existing)
    const replacementDate = calculations.calculateReplacementDate(component, lifetime, building);

    // Get existing draft data OR initialize if missing
    let existingData = maintenanceDraft.components[componentKey];

    // If missing, the component is new to the draft; costs stay template-sourced (computed live) until activation
    if (!existingData) {
        existingData = { maintenanceActivities: {} };
    }

    // Recalculate maintenance dates from condition
    const updatedActivities: Record<string, MaintenanceActivity> = { ...existingData.maintenanceActivities };
    const typeTemplates = maintenanceTemplates[componentType] || [];
    const removed = existingData.removedActivities || {};

    // Activities added by hand from another type's templates follow the condition too;
    // one whose template no longer exists is dropped
    const findTemplate = templateLookup(maintenanceTemplates);
    const templates = [...typeTemplates];
    Object.keys(updatedActivities).forEach(templateId => {
        if (typeTemplates.some(t => t.id === templateId)) return;
        const template = findTemplate(templateId);
        if (template) {
            templates.push(template);
        } else {
            delete updatedActivities[templateId];
        }
    });

    templates.forEach((template: MaintenanceTemplate) => {
        if (removed[template.id]) return;
        const existingActivity = updatedActivities[template.id];

        // Calculate new date from condition
        const nextDate = calculations.calculateNextMaintenanceDate(component, template, lifetime, building);

        if (existingActivity) {
            // Update existing activity - preserve settings, update date
            updatedActivities[template.id] = {
                ...existingActivity,
                nextDate: formatDateToString(nextDate || new Date()),
                lastUpdated: formatDateForStorage(new Date())
            };
        } else if (templateOfferedTo(template, building?.key)) {
            // Create new activity if it doesn't exist (e.g. new template added or new component);
            // a template that belongs to another building is not one of this component's rows
            updatedActivities[template.id] = newActivityEntry(component, template, lifetime, building);
        }
    });

    // Return updated component data - preserve ALL costs, update only dates
    return {
        ...existingData,
        replacementYear: replacementDate?.getFullYear(),
        // replacementCost preserved (initially set above if new)
        maintenanceActivities: updatedActivities
    };
};
